import { bloodPath, connected, type BloodPath, type Graph } from '../graph';
import { birthKey } from '../layout';
import type { Gender } from '../types';
import type { Kin, KinKind, KinStep, Sex, Via } from './types';

export function sexOf(g: Gender | null | undefined): Sex {
  return g === 'female' ? 'f' : g === 'male' ? 'm' : null;
}

type Raw = Pick<KinStep, 'rel' | 'id'>;

/** Older (true) / younger (false) by birth date; null when either date is missing or they are the same. */
function olderThan(g: Graph, a: string, b: string): boolean | null {
  const ka = birthKey(g.people.get(a));
  const kb = birthKey(g.people.get(b));
  if (ka === null || kb === null || ka === kb) return null;
  return ka < kb;
}

/** The one parent two half-siblings share (both have two parents on record), or null when they aren't. */
function sharedParent(g: Graph, a: string, b: string): string | null {
  const pa = g.parents.get(a) ?? [];
  const pb = g.parents.get(b) ?? [];
  const shared = pa.filter((p) => pb.includes(p));
  return pa.length === 2 && pb.length === 2 && shared.length === 1 ? shared[0]! : null;
}

function fill(g: Graph, meId: string, raw: Raw[]): KinStep[] {
  let prev = meId;
  return raw.map((s) => {
    const sibling = s.rel === 'sibling';
    const shared = sibling ? sharedParent(g, s.id, prev) : null;
    const step: KinStep = {
      ...s,
      sex: sexOf(g.people.get(s.id)?.gender),
      elder: sibling ? olderThan(g, s.id, prev) : null,
      half: shared !== null,
      halfVia: shared === null ? null : sexOf(g.people.get(shared)?.gender),
    };
    prev = s.id;
    return step;
  });
}

/** Steps for a blood path: up the parents, across to the sibling line, down the children. */
function bloodSteps(p: BloodPath): Raw[] {
  const { blood, mine, theirs } = p;
  if ('up' in blood) return mine.slice(1).map((id) => ({ rel: 'parent', id }));
  if ('down' in blood) return theirs.slice(0, -1).reverse().map((id) => ({ rel: 'child', id }));
  const ups: Raw[] = mine.slice(1, blood.a).map((id) => ({ rel: 'parent', id }));
  const across: Raw = { rel: 'sibling', id: theirs[blood.b - 1]! };
  const downs: Raw[] = theirs.slice(0, blood.b - 1).reverse().map((id) => ({ rel: 'child', id }));
  return [...ups, across, ...downs];
}

/** Shortest path along parent, child and partner links, with "parent's child" folded into "sibling". */
function shortestPath(g: Graph, from: string, to: string): Raw[] {
  const prev = new Map<string, Raw & { from: string }>();
  const seen = new Set([from]);
  let frontier = [from];
  while (frontier.length && !seen.has(to)) {
    const next: string[] = [];
    for (const id of frontier) {
      const links: [Raw['rel'], string[]][] = [
        ['parent', g.parents.get(id) ?? []],
        ['child', g.children.get(id) ?? []],
        ['spouse', g.partners.get(id) ?? []],
      ];
      for (const [rel, ns] of links) {
        for (const n of ns) {
          if (seen.has(n)) continue;
          seen.add(n);
          prev.set(n, { rel, id: n, from: id });
          next.push(n);
        }
      }
    }
    frontier = next;
  }
  const steps: Raw[] = [];
  for (let cur = to; cur !== from; cur = prev.get(cur)!.from) steps.unshift({ rel: prev.get(cur)!.rel, id: cur });
  const out: Raw[] = [];
  for (const s of steps) {
    const last = out[out.length - 1];
    if (s.rel === 'child' && last?.rel === 'parent') out[out.length - 1] = { rel: 'sibling', id: s.id };
    else out.push(s);
  }
  return out;
}

const LETTERS: Record<KinStep['rel'], [string, string, string]> = {
  parent: ['M', 'F', 'P'],
  child: ['D', 'S', 'C'],
  sibling: ['Z', 'B', 'Sb'],
  spouse: ['W', 'H', 'Sp'],
};

/** The path in kinship notation, e.g. "FeB" (father's elder brother) or "MBS" (mother's brother's son). */
export function pathKey(path: readonly KinStep[]): string {
  return path
    .map((s) => {
      const [f, m, n] = LETTERS[s.rel];
      const letter = s.sex === 'f' ? f : s.sex === 'm' ? m : n;
      const age = s.rel === 'sibling' && s.elder !== null ? (s.elder ? 'e' : 'y') : '';
      return age + letter;
    })
    .join('');
}

/**
 * How `personId` is related to `meId`, without choosing any words. Follows the resolution order in
 * PRD 7.2: self, partner, blood, my partner's blood, their partner's blood, both partners' siblings,
 * step-sibling, co-parent-in-law, then any connection at all.
 */
export function relate(g: Graph, meId: string, personId: string): Kin {
  const o = g.people.get(personId);
  const me = g.people.get(meId);
  const make = (kind: KinKind, raw: Raw[], via: Via = 'blood', shape: Kin['shape'] = null, relative: string | null = null): Kin => {
    const path = fill(g, meId, raw);
    return {
      kind,
      via,
      shape,
      path,
      key: pathKey(path),
      half: path.some((s) => s.half),
      sex: sexOf(o?.gender),
      meSex: sexOf(me?.gender),
      older: o && me && meId !== personId ? olderThan(g, personId, meId) : null,
      relativeOlder: relative ? olderThan(g, relative, meId) : null,
    };
  };
  if (!o || !me) return make('none', []);
  if (meId === personId) return make('self', []);

  const myPartners = g.partners.get(meId) ?? [];
  const theirPartners = g.partners.get(personId) ?? [];
  const spouse = (id: string): Raw => ({ rel: 'spouse', id });

  if (myPartners.includes(personId)) return make('spouse', [spouse(personId)]);

  const direct = bloodPath(g, meId, personId);
  if (direct) return make('blood', bloodSteps(direct), 'blood', direct.blood);

  for (const sp of myPartners) {
    const b = bloodPath(g, sp, personId);
    if (b) return make('marriage', [spouse(sp), ...bloodSteps(b)], 'mine', b.blood);
  }
  for (const os of theirPartners) {
    const b = bloodPath(g, meId, os);
    if (b) return make('marriage', [...bloodSteps(b), spouse(personId)], 'theirs', b.blood, os);
  }
  for (const sp of myPartners) {
    for (const os of theirPartners) {
      const b = bloodPath(g, sp, os);
      if (b && 'a' in b.blood && b.blood.a === 1 && b.blood.b === 1) {
        return make('marriage', [spouse(sp), { rel: 'sibling', id: os }, spouse(personId)], 'both', b.blood);
      }
    }
  }
  // Step-sibling: a child of my parent's partner, sharing no parent with me.
  const myParents = g.parents.get(meId) ?? [];
  const theirParents = g.parents.get(personId) ?? [];
  if (!theirParents.some((x) => myParents.includes(x))) {
    for (const par of myParents) {
      for (const sp of g.partners.get(par) ?? []) {
        if (!myParents.includes(sp) && theirParents.includes(sp)) {
          return make('stepSibling', [{ rel: 'parent', id: par }, spouse(sp), { rel: 'child', id: personId }]);
        }
      }
    }
  }
  // Co-parent-in-law: a parent of a partner of one of my children.
  for (const child of g.children.get(meId) ?? []) {
    for (const cp of g.partners.get(child) ?? []) {
      const cpParents = g.parents.get(cp) ?? [];
      if (cpParents.includes(personId) && !cpParents.includes(meId)) {
        return make('coParentInLaw', [{ rel: 'child', id: child }, spouse(cp), { rel: 'parent', id: personId }]);
      }
    }
  }
  return connected(g, meId, personId) ? make('relative', shortestPath(g, meId, personId)) : make('none', []);
}
